import { useEffect, useMemo, useState } from 'react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { CreateCollectionRequestRequest } from '@weave/schema-gen-ts/dist/schemas/payments-integration/collections/v1/service.pb';
import { _QueryUpdaters, useCreateCollectionRequestMutation } from '@frontend/api-collections';
import { Commands1 } from '@frontend/api-commands';
import { TemplatorV2Hooks } from '@frontend/api-templator-v2';
import { useTranslation } from '@frontend/i18n';
import { useGetDefaultTemplate } from '@frontend/integrated-messaging';
import { paymentsSentry } from '@frontend/payments-hooks';
import { MultiStepModal } from '@frontend/payments-multistep-modal';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, useAlert } from '@frontend/design-system';
import { useBulkCollectionSelectAllShallowStore } from '../../../store/bulk-collections-select-all-store';
import { CollectionColumn } from '../common/generate-columns';
import { getFilterString } from '../utils';
import { BULK_CREATE_STEPS } from './bulk-create-steps-key-title';
import { NonSelectableUsersStep } from './steps/non-selectable-users-step';
import { SelectTemplateStep } from './steps/select-template-step';
import { SendSmsStep } from './steps/send-sms-step';
import { ValidateUsersStep } from './steps/validate-users-step';
import { useValidateUsersShallowStore } from './stores/validate-users-store';
import { mappedTags, buildBulkCreatePayload, derivePatientGroups, getAllPatientsCount } from './utils';

export type BulkCreateStepId = (typeof BULK_CREATE_STEPS)[keyof typeof BULK_CREATE_STEPS]['id'];

type BulkCreateModalProps = {
  modalProps: ModalControlModalProps;
  selected?: CollectionColumn[];
  locationIds: string[];
  initialStep?: BulkCreateStepId;
  useNewApi?: boolean;
};
export const BulkCreateModal = ({
  modalProps,
  selected,
  locationIds,
  initialStep,
  useNewApi,
}: BulkCreateModalProps) => {
  const alert = useAlert();
  const { t } = useTranslation('payments');
  const { invalidateQueries } = Commands1._QueryUpdaters.useQueryUpdaters();
  const { invalidateQueries: invalidateCollectionsV1Queries } = _QueryUpdaters.useQueryUpdaters();
  const getDefaultTemplate = useGetDefaultTemplate({
    groupIds: locationIds,
  });
  const { reset: resetValidateUsersStore } = useValidateUsersShallowStore('reset');
  const {
    selectedCount,
    filters,
    included,
    excluded,
    isAllSelected,
    reset: resetSelectAllStore,
  } = useBulkCollectionSelectAllShallowStore(
    'selectedCount',
    'filters',
    'included',
    'excluded',
    'isAllSelected',
    'reset'
  );

  const [template, setTemplate] = useState(() =>
    getDefaultTemplate({ type: TemplateType_Slug.MANUAL_PAYMENT_REQUEST })
  );

  const { data } = TemplatorV2Hooks.useFilteredTemplateTypes({
    templateTypeSlugs: [TemplateType_Slug.MANUAL_PAYMENT_REQUEST],
  });
  const dynamicFields = data?.find((type) => type.slug === TemplateType_Slug.MANUAL_PAYMENT_REQUEST)?.dynamicFields;

  const handleOnSuccess = (version: 'legacy' | 'new', actionType?: string) => {
    if (actionType === 'CREATE') {
      alert.success(t('Creating payment requests'));
    } else {
      alert.success(t('{{count}} messages being sent.', { count: useNewApi ? selectedCount : selected?.length }));
    }
    if (version === 'new') {
      resetSelectAllStore();
      invalidateCollectionsV1Queries({ endpointName: 'GetPatientBalancesSummary' });
      invalidateCollectionsV1Queries({ endpointName: 'ListPatientBalances' });
    } else {
      invalidateQueries({ endpointName: 'GetPatientsBalance' });
    }
    modalProps.onClose();
  };
  const handleOnError = (err: any) => {
    alert.error(t('Error creating payment requests'));
    paymentsSentry.error(
      'Error creating payment request and sending SMS',
      `BulkCreateModal ${useNewApi ? 'v2' : 'v1'}`,
      `${err instanceof Error ? err.message : 'Unknown error'}`
    );
    modalProps.onClose();
  };
  const { mutate: createPaymentRequestV2, isLoading: isLoadingV2 } = useCreateCollectionRequestMutation({
    options: {
      onSuccess: (_result, request) => {
        handleOnSuccess('new', request.actionType);
      },
      onError: handleOnError,
    },
  });

  const { mutate: createPaymentRequest, isLoading } = Commands1.Mutations.useBatchCreateBalancesPaymentRequestsMutation(
    {
      options: {
        onSuccess: (_result, request) => {
          handleOnSuccess('legacy', request.actionType);
        },
        onError: handleOnError,
      },
    }
  );

  const { data: collections } = Commands1.Queries.useGetPatientsBalanceQuery({
    request: {
      locationIds,
    },
    options: {
      onError: () => {
        alert.error(t('Error fetching collections'));
      },
      enabled: !useNewApi && modalProps.show,
    },
  });

  const { patients, patientsUniqueLocations, patientsWithoutPhone } = useMemo(
    () => derivePatientGroups(selected || collections?.patientBalances || []),
    [selected, collections]
  );
  const [internalSelected, setInternalSelected] = useState<Record<string, boolean>>(
    patients.reduce((acc, patient) => ({ ...acc, [patient.patientId]: true }), {})
  );

  const createAndSendRequest = ({ useTTP, smsBody }: { useTTP?: boolean; smsBody?: string }) => {
    if (useNewApi) {
      const payload: CreateCollectionRequestRequest = {
        locationIds: locationIds,
        includedPersonIds: included,
        excludedPersonIds: excluded,
        actionType: useTTP ? 'CREATE_AND_TTP' : 'CREATE',
        template: {
          templateString: smsBody,
          templateTypeSlug: TemplateType_Slug.MANUAL_PAYMENT_REQUEST,
        },
      };
      if (filters.balanceType.length || filters.searchValue) {
        payload.filter = getFilterString(filters.balanceType, filters.searchValue);
      } else if (isAllSelected) {
        payload.selectAll = true;
      }
      createPaymentRequestV2(payload);
    } else {
      const payload = buildBulkCreatePayload({
        patients,
        internalSelected,
        allPatientsCount: getAllPatientsCount(collections?.patientBalances),
        patientsUniqueLocations,
        useTTP,
        smsBody,
      });
      createPaymentRequest(payload);
    }
  };

  useEffect(() => {
    if (modalProps.show) {
      setTemplate(getDefaultTemplate({ type: TemplateType_Slug.MANUAL_PAYMENT_REQUEST }));
      resetValidateUsersStore();
    }
  }, [modalProps.show]);

  return (
    <MultiStepModal
      as={Modal.Responsive}
      modalProps={modalProps}
      closeModal={modalProps.onClose}
      initialStep={initialStep ?? BULK_CREATE_STEPS.SEND_SMS.id}
      maxWidth={parseFloat(theme.spacing(97))}
      hideBackButton
    >
      <MultiStepModal.Step id={BULK_CREATE_STEPS.VALIDATE_USERS.id} title={BULK_CREATE_STEPS.VALIDATE_USERS.title}>
        <ValidateUsersStep
          useNewApi={!!useNewApi}
          patients={patients}
          onSelectionChange={setInternalSelected}
          selected={internalSelected}
        />
      </MultiStepModal.Step>
      <MultiStepModal.Step id={BULK_CREATE_STEPS.SEND_SMS.id} title={BULK_CREATE_STEPS.SEND_SMS.title}>
        <SendSmsStep
          count={useNewApi ? selectedCount : Object.values(internalSelected).filter(Boolean).length}
          text={template?.templateString ?? ''}
          onSend={createAndSendRequest}
          isLoading={useNewApi ? isLoadingV2 : isLoading}
          tags={dynamicFields ? mappedTags(dynamicFields) : []}
        />
      </MultiStepModal.Step>
      <MultiStepModal.Step id={BULK_CREATE_STEPS.SELECT_TEMPLATE.id} title={BULK_CREATE_STEPS.SELECT_TEMPLATE.title}>
        <SelectTemplateStep locationIds={locationIds} onSelectTemplate={setTemplate} />
      </MultiStepModal.Step>
      <MultiStepModal.Step
        id={BULK_CREATE_STEPS.NON_SELECTABLE_USERS.id}
        title={t('{{count}} Balances', { count: patientsWithoutPhone.length })}
      >
        <NonSelectableUsersStep data={patientsWithoutPhone} />
      </MultiStepModal.Step>
    </MultiStepModal>
  );
};
